import { Component, OnDestroy, OnInit, effect, ViewChild } from '@angular/core';
import { Table, TableModule } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { ButtonModule } from 'primeng/button';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Conditionnement } from './conditionnement.model';
import { ReferentialsConditionnementsService } from './referentials-conditionnements.service';
import { AddConditionnementComponent } from './add-conditionnement/add-conditionnement.component';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { StoreService } from '../../../../core/services/store.service';
import { HttpErrorResponse } from '@angular/common/http';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

@Component({
  selector: 'app-referentials-conditionnements',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, InputTextModule,ReactiveFormsModule, FormsModule, ToastModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService],
  templateUrl: './referentials-conditionnements.component.html',
  styleUrl: './referentials-conditionnements.component.css',
})
export class ReferentialsConditionnementsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('conditionnementTable') table!: Table;

  userHasReadWritePermission: boolean = false;

  selectedConditionnement?: Conditionnement;
  conditionnementResult!: Conditionnement[];
  loading: boolean = false;

  ref: DynamicDialogRef | undefined;

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];

  totalRecords: number = 0;
  editRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingLine: any = null;
  clonedConditionnement: { [s: string]: Conditionnement } = {};
  condiForm = this.formBuilder.group({
      id: this.formBuilder.control<Number | null>(null),
      codeHecate: this.formBuilder.control<Number | null>(null),
      designation: ['']
  });

  constructor(
    private conditionnementService: ReferentialsConditionnementsService,
    private storeService: StoreService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadConditionnement();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadConditionnement() {
    this.loading = true;
    this.conditionnementService
      .getConditionnementData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.conditionnementResult = data;
        this.loading = false;
      });
  }

  ajouterConditionnement() {
    this.ref = this.dialogService.open(AddConditionnementComponent, {
      ...dialogConfig,
      header: 'Ajouter une nouvelle ligne',
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadConditionnement();
      }
    });
  }

  modifierConditionnement(conditionnement: Conditionnement, index: number) {
    this.editRowIndex = index;
    this.editingLine = conditionnement;
    this.selectedConditionnement = conditionnement;
    const id = conditionnement.id;
    this.condiForm.controls.codeHecate.patchValue(conditionnement.codeHecate);
    this.condiForm.controls.id.patchValue(conditionnement.id);
    this.condiForm.controls.designation.patchValue(conditionnement.designation);
    this.clonedConditionnement[id.toString()] = { ...conditionnement };
  }

  validerModifsConditionnement(conditionnement: Conditionnement) {
    this.conditionnementService.updateConditionnementData(this.condiForm.value as Conditionnement).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsConditionnement(conditionnement: Conditionnement, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = conditionnement.id;
    this.condiForm.controls.codeHecate.patchValue(conditionnement.codeHecate);
    this.condiForm.controls.id.patchValue(conditionnement.codeHecate);
    this.condiForm.controls.designation.patchValue(conditionnement.designation);
    this.conditionnementResult.filter(c=>c.id===id).map(condi => {
      condi = this.clonedConditionnement[id.toString()];
    });
    delete this.clonedConditionnement[id.toString()];
  }

  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.showSuccessMessage('La ligne a été modifiée avec succès.');
      this.loadConditionnement();
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  onRowSelect(data: Conditionnement) {
    this.selectedConditionnement = data;
  }

  showInfoMessage(infoMessage: string) {
    this.messageService.add({ severity: 'info', summary: 'Information', detail: infoMessage, life: 3000 });
  }

  showSuccessMessage(successMessage: string) {
    this.messageService.add({ severity: 'success', summary: 'Succès', detail: successMessage, life: 3000 });
  }

  showErrorMessage(errorMessage: string) {
    this.messageService.add({ severity: 'error', summary: 'Erreur', detail: errorMessage, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
